import { useTranslation } from "react-i18next";

import { ProjectStage } from "../../../../../../interfaces/Projects/Catalogs/projectStages";
import GenericPromises from "../../../../../../api/GenericPromises";
import useSnackBar from "../../../../../../components/Commons/SnackBar/useSnackBar";
import { FormsErrors } from "../../../../../../hooks/Forms/FormsErrors";
import { useEffect, useState } from "react";
import { Stage } from "../../../../../../interfaces/Projects/Catalogs/stages";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { Spinner } from "../../../../../../components/Commons/Spinner/Spinner";
import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { myStylePickers, PrimaryButton } from "../../../../../../theme/buttons";
import dayjs from "dayjs";
import { GenericDialog } from "../../../../../../components/Dialogs/Dialog";
import { DialogEntity } from "../../../../../../components/Dialogs/DialogEntity";
import { Header } from "../../../../../../components/Header";
import { ConfirmDialog } from "./confirmDialog";
import { ConfirmChangeProjectStageConsecutiveDialog } from "../components/dialogs/confirmChangeProjectStageConsecutiveDialog";
import { Menuitem } from "../../../../../../interfaces/Security/menu";
import { FieldType } from "../../../../../../interfaces/Projects/Catalogs/fieldTypes";
import { useProjectStagesStore } from "../../../../../../store/Projects/ProjectDetails/ProjectStagesStore";
import { useProjectTasksStore } from "../../../../../../store/Projects/ProjectDetails/ProjectTasksStore";

type UpdateProjectStagesProps = {
  permissions: Menuitem;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  projectStagePayload: ProjectStage;
  comboFieldTypes: FieldType[];
}

export const UpdateProjectStages = ({
  permissions,
  setOpenDialog,
  projectStagePayload,
  comboFieldTypes,
}: UpdateProjectStagesProps) => {
  const [t] = useTranslation("global");
  const {
    GenericPutResource,
    GenericDeleteResource,
    GenericGetResource,
    GenericPostResource,
  } = GenericPromises();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { GetError } = FormsErrors();
  const [loadingPut, setLoadingPut] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [comboStages, setComboStages] = useState<Stage[]>([]);
  const isEdit = !permissions.update;
  const [defaultStage, setDefaultStage] = useState<Stage>();
  const [openStartDateDatePicker, setOpenStartDateDatePicker] = useState(false);
  const [openEndDateDatePicker, setOpenEndDateDatePicker] = useState(false);
  const [dialogConfirmDeleteStage, setDialogConfirmDeleteStage] = useState(false);
  const [inputStage, setInputStage] = useState("");
  const [dialogConfirmChangeProjectStageConsecutive, setDialogConfirmChangeProjectStageConsecutive] = useState(false);
  const [initialConsecutive, setInitialConsecutive] = useState<string>();
  const [isTypeFormulaStartedDate, setIsTypeFormulaStartedDate] = useState(false);
  const [isTypeFormulaDueDate, setIsTypeFormulaDueDate] = useState(false);
  const [defaultProjectStageStartedDate, setDefaultProjectStageStartedDate] = useState<string | Date>()
  const [defaultStartedDateFieldTypeId, setDefaultStartedDateFieldTypeId] = useState<FieldType>()
  const [defaultProjectStageDueDate, setDefaultProjectStageDueDate] = useState<string | Date>()
  const [defaultDueDateFieldTypeId, setDefaultDueDateFieldTypeId] = useState<FieldType>();
  const updateProjectStage = useProjectStagesStore(store => store.updateProjectStage);
  const deleteProjectStage = useProjectStagesStore(store => store.deleteProjectStage);
  const markTasksInStageAsIncomplete = useProjectTasksStore(store => store.markTasksInStageAsIncomplete);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
    getValues
  } = useForm<ProjectStage>();

  const startedDateFieldTypeId = useWatch({
    control,
    name: "project_stage_started_date_field_type_id"
  });

  const projectStageStartedDate = useWatch({
    control,
    name: "project_stage_started_date"
  });

  const dueDateFieldTypeId = useWatch({
    control,
    name: "project_stage_due_date_field_type_id"
  });

  // const onSubmit: SubmitHandler<ProjectStage> = (data) => onPut(data);

  const onPut = (data: ProjectStage) => {
    let myData = {
      project_id: projectStagePayload.project_id,
      stage_id: data.stage_id,
      project_stage_name: data.project_stage_name,
      project_stage_consecutive:
        data.project_stage_consecutive,
      project_stage_description:
        data.project_stage_description || null,
      project_stage_started_date: data.project_stage_started_date || null,
      project_stage_due_date: data.project_stage_due_date || null,
      project_stage_started_date_field_type_id: data.project_stage_started_date_field_type_id,
      project_stage_due_date_field_type_id: data.project_stage_due_date_field_type_id
    };
    setLoadingPut(true);
    GenericPutResource(
      `/projectstages/evaluated/${projectStagePayload.project_stage_id}`,
      myData
    )
      .then((response) => {
        const projectStages: ProjectStage[] = response.data.data;
        projectStages.forEach((projectStage: ProjectStage) => {
          updateProjectStage(projectStage)
          if(projectStage.project_stage_id){
            markTasksInStageAsIncomplete(projectStage.project_stage_id)
          }
        });


        showSnackBar(t("generic.snackbar.update"), "success");
        setLoadingPut(false);
        setOpenDialog(false);
        setInitialConsecutive(data.project_stage_consecutive)
      })
      .catch((error) => {
        showSnackBar(error.message, "error");
        setLoadingPut(false);
      });
  };


  const handleUpdate = (data: any) => {
    const currentConsecutive = getValues("project_stage_consecutive");

    if (currentConsecutive !== initialConsecutive) {
      setDialogConfirmChangeProjectStageConsecutive(true);
    } else {
      onPut(data);
    }
  };

  const restorePreviousValue = () => {
    setValue("project_stage_consecutive", initialConsecutive);
  };

  const onDelete = () => {
    GenericDeleteResource(
      `/projectstages/${projectStagePayload.project_stage_id}`
    )
      .then(() => {
        setOpenDialog(false);
        if (projectStagePayload.project_stage_id) {
          deleteProjectStage(
            projectStagePayload.project_stage_id
          );
        }
        setOpenDialog(false);
      })
      .catch((error) => {
        showSnackBar(error.message, "error");
        setOpenDialog(false);
      });
  };


  const handleAddStage = (stage_name: string) => {
    const existingStage = comboStages.find(
      (stage) => stage.stage_name === stage_name
    );
    if (existingStage) return existingStage;

    let myData = {
      stage_name: stage_name,
      stage_description: null,
    };
    setLoadingPut(true);
    GenericPostResource("/stages", myData)
      .then((response) => {
        setComboStages([...comboStages, response.data]);
        setLoadingPut(false);
        return response.data;
      })
      .catch((error) => {
        showSnackBar(error.message, "error");
      });
  };

  const disableKeyboardEntry = (e: any) => {
    if (e?.preventDefault) {
      e?.preventDefault();
      e?.stopPropagation();
    }
  };

  useEffect(() => {
    let myPromises = [GenericGetResource("/stages")];
    Promise.all(myPromises)
      .then((responses) => {
        setComboStages(responses[0].data.data);
        reset({
          project_id: projectStagePayload.project_id,
          stage_id: projectStagePayload.stage_id,
          project_stage_name:
            projectStagePayload.project_stage_name,
          project_stage_consecutive:
            projectStagePayload.project_stage_consecutive,
          project_stage_description:
            projectStagePayload.project_stage_description,
          project_stage_started_date: projectStagePayload?.project_stage_started_date || undefined,
          project_stage_due_date: projectStagePayload?.project_stage_due_date || undefined,
          project_stage_started_date_field_type_id: projectStagePayload.project_stage_started_date_field_type_id,
          project_stage_due_date_field_type_id: projectStagePayload.project_stage_due_date_field_type_id
        });
        setDefaultStage(
          responses[0].data.data.find(
            (stage: Stage) =>
              stage.stage_id === projectStagePayload.stage_id
          )
        );
        setInitialConsecutive(projectStagePayload.project_stage_consecutive);

        setDefaultProjectStageStartedDate(
          projectStagePayload.project_stage_started_date
        );

        const startedDateFieldTypeId = comboFieldTypes.find(
          (fielType: FieldType) =>
            fielType.field_type_id === projectStagePayload.project_stage_started_date_field_type_id
        )

        setDefaultStartedDateFieldTypeId(startedDateFieldTypeId);

        if (startedDateFieldTypeId?.field_type_name.toLowerCase() == 'formula') {
          setIsTypeFormulaStartedDate(true);
        }

        setDefaultProjectStageDueDate(
          projectStagePayload.project_stage_due_date
        );

        const dueDateFieldTypeId = comboFieldTypes.find(
          (fielType: FieldType) =>
            fielType.field_type_id === projectStagePayload.project_stage_due_date_field_type_id
        )

        setDefaultDueDateFieldTypeId(dueDateFieldTypeId);

        if (dueDateFieldTypeId?.field_type_name.toLowerCase() == 'formula') {
          setIsTypeFormulaDueDate(true);
        }
        setDataLoaded(true);
      })
      .catch((error) => {
        showSnackBar(error.message, "error");
      });
  }, []);

  return (
    <>
      {!dataLoaded && (
        <div className=" h-25">
          <Spinner isBox={false} />
        </div>
      )}
      {dataLoaded && (
        <Box className="d-flex justify-content-center" sx={{ minWidth: 1000 }}>
          <form onSubmit={handleSubmit(handleUpdate)} className="w-100">
            <div className="d-flex mt-1">
              <div className="w-50">
                <Controller
                  name="stage_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      ref={field.ref}
                      size="small"
                      sx={{ width: "100%", paddingRight: 2 }}
                      options={comboStages}
                      defaultValue={defaultStage}
                      getOptionLabel={(option) => `${option.stage_name}`}
                      renderOption={(props, option: Stage) => (
                        <div key={option.stage_id}>
                          <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            {...props}
                          >
                            {option.stage_name}
                          </Box>
                        </div>
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.stage_id === value.stage_id
                      }
                      onInputChange={(_, newInputValue) => {
                        setInputStage(newInputValue);
                      }}
                      onChange={(_, values) => {
                        field.onChange(values?.stage_id || null);
                      }}
                      noOptionsText={
                        <Button
                          onMouseDown={() => {
                            if (inputStage.trim()) {
                              const newStage = handleAddStage(inputStage);
                              field.onChange(newStage?.stage_id || null);
                            }
                          }}
                        >
                          {t("projectstages.actions.create")}
                        </Button>
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t("projectstages.fields.stage")}`}
                          variant="filled"
                          value={field.value}
                          error={!!errors.stage_id}
                          helperText={GetError(errors.stage_id?.type)}
                          disabled={isEdit}
                        />
                      )}
                    />
                  )}
                />
              </div>
              <div className="w-50">
                <Controller
                  name="project_stage_name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      variant="filled"
                      label={`${t(
                        "projectstages.fields.project_stage_name"
                      )}`}
                      ref={field.ref}
                      sx={{
                        paddingRight: 2,
                      }}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                      error={!!errors.project_stage_name}
                      helperText={GetError(
                        errors.project_stage_name?.type
                      )}
                      size="small"
                      style={{ width: "100%" }}
                      disabled={isEdit}
                    />
                  )}
                />
              </div>
            </div>
            <div className="d-flex mt-1 pt-3">
              <div className="w-50">
                <Controller
                  name="project_stage_consecutive"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      variant="filled"
                      label={`${t(
                        "projectstages.fields.project_stage_consecutive"
                      )}`}
                      ref={field.ref}
                      sx={{
                        paddingRight: 2,
                      }}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                      error={!!errors.project_stage_consecutive}
                      helperText={GetError(
                        errors.project_stage_consecutive?.type
                      )}
                      size="small"
                      style={{ width: "100%" }}
                      disabled={isEdit}
                    />
                  )}
                />
              </div>

              <div className="w-50">
                <Controller
                  name="project_stage_description"
                  control={control}
                  rules={{}}
                  render={({ field }) => (
                    <TextField
                      variant="filled"
                      label={`${t(
                        "projectstages.fields.project_stage_description"
                      )}`}
                      ref={field.ref}
                      sx={{
                        paddingRight: 2,
                      }}
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                      error={!!errors.project_stage_description}
                      helperText={GetError(
                        errors.project_stage_description?.type
                      )}
                      size="small"
                      style={{ width: "100%" }}
                      disabled={isEdit}
                    />
                  )}
                />
              </div>
            </div>
            <div className="d-flex mt-1 pt-3">
              <div className="w-50">
                <Controller
                  name="project_stage_started_date_field_type_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      ref={field.ref}
                      size="small"
                      sx={{ width: "100%", paddingRight: 2 }}
                      options={comboFieldTypes}
                      defaultValue={defaultStartedDateFieldTypeId}
                      getOptionLabel={(option) => `${option.field_type_name}`}
                      renderOption={(props, option: FieldType) => (
                        <div key={option.field_type_id}>
                          <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            {...props}
                          >
                            {option.field_type_name}
                          </Box>
                        </div>
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.field_type_id === value.field_type_id
                      }
                      onChange={async (_, values) => {
                        field.onChange(values?.field_type_id || null);
                        if (
                          comboFieldTypes.find(
                            (item) =>
                              item.field_type_id === values?.field_type_id &&
                              item.field_type_name.toLowerCase() === "formula"
                          )
                        ) {
                          setIsTypeFormulaStartedDate(true);
                        } else {
                          setIsTypeFormulaStartedDate(false);
                        }
                        setValue("project_stage_started_date", "", {
                          shouldValidate: true,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t(
                            "projectstages.fields.project_stage_started_date_field_type_id"
                          )}`}
                          variant="filled"
                          value={field.value}
                          error={!!errors.project_stage_started_date_field_type_id}
                          helperText={GetError(errors.project_stage_started_date_field_type_id?.type)}
                          disabled={isEdit}
                        />
                      )}
                    />
                  )}
                />
              </div>
              {isTypeFormulaStartedDate ? (
                <div className="w-50">
                  <Controller
                    name="project_stage_started_date"
                    control={control}
                    rules={{ maxLength: 50 }}
                    render={({ field }) => (
                      <TextField
                        variant="filled"
                        label={`${t(
                          "projectstages.fields.project_stage_started_date"
                        )}`}
                        ref={field.ref}
                        value={field.value || ""}
                        defaultValue={defaultProjectStageStartedDate}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                        }}
                        error={!!errors.project_stage_started_date}
                        helperText={GetError(
                          errors.project_stage_started_date?.type
                        )}
                        size="small"
                        style={{ width: "100%" }}
                        disabled={!startedDateFieldTypeId || isEdit ? true : false}
                      />
                    )}
                  />
                </div>
              ) : (
                <div className="w-50">
                  <Controller
                    name="project_stage_started_date"
                    control={control}
                    rules={{}}
                    render={({ field }) => (
                      <DatePicker
                        open={openStartDateDatePicker}
                        onClose={() => setOpenStartDateDatePicker(false)}
                        ref={field.ref}
                        label={`${t(
                          "projectstages.fields.project_stage_started_date"
                        )}`}
                        onChange={(values: any) => {
                          field.onChange(values?.toDate() || null);
                        }}
                        value={field.value ? dayjs(field.value) : null}
                        slotProps={{
                          popper: {
                            sx: myStylePickers
                          },
                          textField: {
                            variant: "filled",
                            size: "small",
                            fullWidth: true,
                            onClick: () => setOpenStartDateDatePicker(true),
                            onBeforeInput: disableKeyboardEntry,
                            error:
                              !!errors.project_stage_started_date,
                            helperText: GetError(
                              errors.project_stage_started_date?.type
                            ),
                          },
                        }}
                        format="DD/MM/YYYY"
                        disabled={!startedDateFieldTypeId || isEdit ? true : false}
                      />
                    )}
                  />
                </div>
              )}
            </div>
            <div className="d-flex mt-1 pt-3">
              <div className="w-50">
                <Controller
                  name="project_stage_due_date_field_type_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      ref={field.ref}
                      size="small"
                      sx={{ width: "100%", paddingRight: 2 }}
                      options={comboFieldTypes}
                      defaultValue={defaultDueDateFieldTypeId}
                      getOptionLabel={(option) => `${option.field_type_name}`}
                      renderOption={(props, option: FieldType) => (
                        <div key={option.field_type_id}>
                          <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            {...props}
                          >
                            {option.field_type_name}
                          </Box>
                        </div>
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.field_type_id === value.field_type_id
                      }
                      onChange={async (_, values) => {
                        field.onChange(values?.field_type_id || null);
                        if (
                          comboFieldTypes.find(
                            (item) =>
                              item.field_type_id === values?.field_type_id &&
                              item.field_type_name.toLowerCase() === "formula"
                          )
                        ) {
                          setIsTypeFormulaDueDate(true);
                        } else {
                          setIsTypeFormulaDueDate(false);
                        }
                        setValue("project_stage_due_date", "", {
                          shouldValidate: true,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t(
                            "projectstages.fields.project_stage_due_date_field_type_id"
                          )}`}
                          variant="filled"
                          value={field.value}
                          error={!!errors.project_stage_due_date_field_type_id}
                          helperText={GetError(errors.project_stage_due_date_field_type_id?.type)}
                          disabled={isEdit}
                        />
                      )}
                    />
                  )}
                />
              </div>
              {isTypeFormulaDueDate ? (
                <div className="w-50">
                  <Controller
                    name="project_stage_due_date"
                    control={control}
                    rules={{ maxLength: 50 }}
                    render={({ field }) => (
                      <TextField
                        variant="filled"
                        label={`${t(
                          "projectstages.fields.project_stage_due_date"
                        )}`}
                        ref={field.ref}
                        value={field.value || ""}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                        }}
                        error={!!errors.project_stage_due_date}
                        helperText={GetError(
                          errors.project_stage_due_date?.type
                        )}
                        size="small"
                        style={{ width: "100%" }}
                        disabled={!dueDateFieldTypeId || isEdit ? true : false}
                      />
                    )}
                  />
                </div>
              ) : (
                <div className="w-50">
                  <Controller
                    name="project_stage_due_date"
                    control={control}
                    rules={{}}
                    render={({ field }) => (
                      <DatePicker
                        open={openEndDateDatePicker}
                        onClose={() => setOpenEndDateDatePicker(false)}
                        ref={field.ref}
                        label={`${t(
                          "projectstages.fields.project_stage_due_date"
                        )}`}
                        onChange={(values: any) => {
                          field.onChange(values?.toDate() || null);
                        }}
                        value={field.value ? dayjs(field.value) : null}
                        slotProps={{
                          popper: {
                            sx: myStylePickers
                          },
                          textField: {
                            variant: "filled",
                            size: "small",
                            fullWidth: true,
                            onClick: () => setOpenEndDateDatePicker(true),
                            onBeforeInput: disableKeyboardEntry,
                            error:
                              !!errors.project_stage_due_date,
                            helperText: GetError(
                              errors.project_stage_due_date?.type
                            ),
                          },
                        }}
                        minDate={dayjs(projectStageStartedDate)}
                        format="DD/MM/YYYY"
                        disabled={!dueDateFieldTypeId || isEdit ? true : false}
                      />
                    )}
                  />
                </div>
              )}
            </div>

            <div className="d-flex flex-row-reverse mt-4 justify-content-between">
              <div>
                <Button
                  variant="outlined"
                  className="m-1"
                  onClick={() => setOpenDialog(false)}
                >
                  {t("generic.buttons.goback")}
                </Button>
                {permissions.update && (
                  <PrimaryButton
                    type="submit"
                    variant="outlined"
                    className="m-1"
                    disabled={loadingPut}
                  >
                    {t("generic.buttons.update")}
                  </PrimaryButton>
                )}
              </div>
              {permissions.delete ? (
                <PrimaryButton onClick={() => setDialogConfirmDeleteStage(true)}>
                  {t("generic.buttons.delete")}
                </PrimaryButton>
              ) : (
                <></>
              )}
            </div>
          </form>
        </Box>
      )}
      <GenericDialog
        title={t("generic.dialog.delete")}
        content={t("projectstages.dialog.delete")}
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        onAcept={onDelete}
      />
      <DialogEntity
        open={dialogConfirmDeleteStage}
        title={<Header title={t("projectstages.title-view")} />}
        maxWidth={"sm"}
        fullWidth
        content={
          <ConfirmDialog
            getValues={getValues}
            isEdit={isEdit}
            loadingPost={loadingPut}
            setDialogConfirmDeleteStage={setDialogConfirmDeleteStage}
            onDelete={onDelete}
          />
        }
      />
      <DialogEntity
        open={dialogConfirmChangeProjectStageConsecutive}
        title={<Header title={"Cambio de consecutivo de etapa"} />}
        maxWidth={"sm"}
        fullWidth
        content={
          <ConfirmChangeProjectStageConsecutiveDialog
            isEdit={isEdit}
            loadingPut={loadingPut}
            setDialogConfirmChangeProjectStageConsecutive={setDialogConfirmChangeProjectStageConsecutive}
            onPut={(data) => {
              setInitialConsecutive(getValues("project_stage_consecutive"));
              onPut(data);
            }}
            restorePreviousValue={restorePreviousValue}
            getValues={getValues}
          />
        }
      />
      <SnackbarComponent />
    </>
  );
};
